<script setup>
/**
 * One word for where a message your own systems asked for actually got to.
 *
 * TWO OF THE NINE ARE NOT FAILURES AND MUST NOT BE COLOURED AS ONE. `suppressed` means we
 * decided not to send — nothing went wrong, and the detail page names the rule that
 * decided it. `unconfirmed` means we asked and never heard back, which WhatsApp may
 * nonetheless have delivered and charged for; calling it a failure invites paying twice.
 * The labels in `WASLO.OUTBOUND.STATUS.*` say both out loud, and the tones agree with
 * them: only `failed` is ruby, only `unconfirmed` is amber, and the other seven are the
 * neutral base. A status colour that fires when nothing is wrong is a colour nobody reads
 * when something is (README §4).
 *
 * COLOUR IS NEVER THE ONLY CUE, for the same reason the credit chip carries `.sr-only`
 * severity text (README §7). Here the cue is cheaper than that: the chip's own word IS
 * the signal — "Failed" reads as failed in a screen reader, in monochrome and to a
 * red-green eye — and the icon on the two states that carry severity is the second,
 * non-colour half for anyone scanning a list of fifty rows.
 *
 * AN UNRECOGNISED STATUS RENDERS AS ITSELF. `api/client.js`'s reader would refuse it long
 * before this chip saw it (`oneOf` throws), so today this branch is unreachable — but a
 * chip whose whole job is naming a state must not be the thing that invents a confident
 * wrong name for one, and nothing here depends on that reader staying strict. It gets the
 * raw wire word, the amber that means "look at this", and a width cap, because an
 * unrecognised value is also an unbounded one (README §2).
 */
import { computed } from 'vue';

import { useWasloI18n } from '../../i18n.js';

const props = defineProps({
  /** One of `WASLO_OUTBOUND_STATUSES`, or anything at all — see above. */
  status: { type: String, required: true },
});

const { t } = useWasloI18n();

const KEY = {
  queued: 'QUEUED',
  sending: 'SENDING',
  accepted: 'ACCEPTED',
  sent: 'SENT',
  delivered: 'DELIVERED',
  read: 'READ',
  failed: 'FAILED',
  unconfirmed: 'UNCONFIRMED',
  suppressed: 'SUPPRESSED',
};

const QUIET = 'bg-n-alpha-2 text-n-slate-11';

const TONE = {
  failed: 'bg-n-ruby-3 text-n-ruby-11',
  unconfirmed: 'bg-n-amber-3 text-n-amber-11',
};

const ICON = {
  failed: 'i-lucide-triangle-alert',
  unconfirmed: 'i-lucide-circle-help',
};

const known = computed(() => KEY[props.status] !== undefined);

const label = computed(() =>
  known.value ? t(`WASLO.OUTBOUND.STATUS.${KEY[props.status]}`) : props.status
);

const tone = computed(() => {
  if (!known.value) return TONE.unconfirmed;
  return TONE[props.status] ?? QUIET;
});

const icon = computed(() => {
  if (!known.value) return ICON.unconfirmed;
  return ICON[props.status] ?? '';
});
</script>

<template>
  <span
    class="inline-flex items-center h-6 gap-1 px-2 text-xs font-medium rounded-md shrink-0 max-w-full"
    :class="tone"
    :data-waslo-outbound-status="status"
  >
    <span v-if="icon" class="size-3" :class="icon" aria-hidden="true" />
    <span class="truncate">{{ label }}</span>
  </span>
</template>
